import React, { useEffect, useRef, useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EnergyEfficiency } from '../../../../../../services/types/Calculator';
import { Modal } from '../../../../../../shared/components';
import { calculateEnergyEfficiency } from '../../Utils/moduleCalculates';
import { handleKeyDown } from '../../Utils/KeyboardHandlers';
import EnergyEfficiencyDetails from './Details/EnergyEfficiencyDetails';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';

import CalculatorTableWrapper from '../Shared/CalculatorTableWrapper';
import { tooltipPreventOverflowModifiers } from '../../../../../../shared/misc/utils';

interface Props {
  energyEfficiency: EnergyEfficiency;
  onUpdateEnergyEfficiency: (updatedStreetlights: EnergyEfficiency) => void;
}

const EnergyEfficiencySection: React.FC<Props> = ({
  energyEfficiency,
  onUpdateEnergyEfficiency,
}) => {
  const { t } = useTranslation();

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [totalCarbonSum, setTotalCarbonSum] = useState<number | null>(null);
  const [defaultUnits, setDefaultUnits] = useState<number | null>(energyEfficiency.defaultUnit);
  const [inputValue, setInputValue] = useState<number | null>(
    energyEfficiency.isCustomWattage
      ? energyEfficiency.customWattageReplacement
      : energyEfficiency.defaultInputValue
  );
  const [isInputDisabled, setIsInputDisabled] = useState<boolean>(true);
  const [isCustomWattage, setIsCustomWattage] = useState<boolean>(energyEfficiency.isCustomWattage);

  const inputEnergyRef = useRef<HTMLInputElement | null>(null);

  const regex = /^[0-9]*\.?[0-9]*$/;

  // Inicializar totalCarbonSum al montar el componente
  useEffect(() => {
    if (energyEfficiency.subTotal !== null && energyEfficiency.subTotal !== undefined) {
      setTotalCarbonSum(energyEfficiency.subTotal);
    }
  }, []);

  useEffect(() => {
    const units = Number(energyEfficiency.unitsReplaced);
    const wattage = Number(energyEfficiency.wattageConventional);
    const hours = Number(energyEfficiency.hoursDay);

    let wattsValue: number | null;
    if (isCustomWattage && energyEfficiency.customWattageReplacement !== null) {
      wattsValue = energyEfficiency.customWattageReplacement;
    } else {
      wattsValue = inputValue !== null ? inputValue : defaultUnits;
    }

    if (units && wattage && hours && wattsValue !== null) {
      const resolvedWattsValue = inputValue !== null ? inputValue : defaultUnits;
      const result = calculateEnergyEfficiency(
        units,
        wattage,
        Number(resolvedWattsValue),
        hours,
        energyEfficiency.countryFactor
      );

      onUpdateEnergyEfficiency({
        ...energyEfficiency,
        unitsReplaced: units,
        wattageConventional: wattage,
        hoursDay: hours,
        moduleId: 0,
        details: result,
        subTotal: result.avoidedEmissions,
      });

      setTotalCarbonSum(result.avoidedEmissions);
    } else {
      setTotalCarbonSum(null);
      onUpdateEnergyEfficiency({
        ...energyEfficiency,
        unitsReplaced: energyEfficiency.unitsReplaced,
        wattageConventional: energyEfficiency.wattageConventional,
        hoursDay: energyEfficiency.hoursDay,
        moduleId: energyEfficiency.moduleId,
        subTotal: null,
      });
    }
  }, [
    energyEfficiency.unitsReplaced,
    energyEfficiency.wattageConventional,
    energyEfficiency.customWattageReplacement,
    energyEfficiency.hoursDay,
    energyEfficiency.subTotal,
    defaultUnits,
    inputValue,
  ]);

  const handleInputHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (!regex.test(value)) {
      return;
    }

    if (value.length > 10) {
      return;
    }

    const numericValue = value !== '' ? Number(value) : null;

    if (numericValue !== null && numericValue > 24) {
      return;
    }

    onUpdateEnergyEfficiency({
      ...energyEfficiency,
      hoursDay: numericValue,
    });
  };

  const handleInputUnitsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    onUpdateEnergyEfficiency({
      ...energyEfficiency,
      unitsReplaced: value === '' ? null : Number(value),
    });
  };

  const handleBlurInput = () => {
    setIsInputDisabled(true);
    if (inputValue === null && defaultUnits !== null) {
      setInputValue(defaultUnits);
      setIsCustomWattage(false);
      onUpdateEnergyEfficiency({
        ...energyEfficiency,
        isCustomWattage: false,
      });
    }
  };

  const handleInputWattage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    onUpdateEnergyEfficiency({
      ...energyEfficiency,
      customWattageReplacement: value === '' ? null : Number(value),
      isCustomWattage: true,
    });
    if (energyEfficiency.customWattageReplacement === null) {
      setTotalCarbonSum(null);
    }

    setInputValue(value === '' ? null : Number(value));

    setIsCustomWattage(true);
  };

  const findValueByCantidades = (value: number) => {
    const { factors } = energyEfficiency;
    for (let i = 0; i < factors.length; i += 1) {
      const current = factors[i];
      const next = factors[i + 1];

      if (value >= current.conventionalUnit && (!next || value < next.conventionalUnit)) {
        return current.cflUnit;
      }
    }
    return null;
  };

  const handleInputWattageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    const factor = findValueByCantidades(Number(value));
    setDefaultUnits(factor);

    onUpdateEnergyEfficiency({
      ...energyEfficiency,
      wattageConventional: value === '' ? null : Number(value),
      defaultInputValue: factor,
      defaultUnit: factor,
    });

    if (isCustomWattage !== true) {
      setInputValue(factor);
    }
  };

  useEffect(() => {
    if (isCustomWattage !== true) {
      setInputValue(energyEfficiency.defaultInputValue);
    }

    setDefaultUnits(energyEfficiency.defaultUnit);
  }, [energyEfficiency.defaultInputValue, energyEfficiency.defaultUnit]);

  const setCustomArea = () => {
    setIsCustomWattage(true);
    onUpdateEnergyEfficiency({
      ...energyEfficiency,
      isCustomWattage: true,
      customWattageReplacement: defaultUnits,
    });
    setIsInputDisabled(false);
  };

  useEffect(() => {
    if (!isInputDisabled && inputEnergyRef.current) {
      inputEnergyRef.current.focus();
    }
  }, [isInputDisabled]);

  return (
    <>
      <div className="content-calculator">
        <br />
        <div className="row calculator-input-row">
          <div className="col-12 col-sm-5 col-md-3 col-lg-3">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Number of Conventional Units Replaced')}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-4 col-lg-2">
            <input
              title="Number of Conventional Units Replaced"
              type="number"
              id="numberUnitsInput"
              value={
                energyEfficiency.unitsReplaced !== null &&
                energyEfficiency.unitsReplaced !== undefined
                  ? energyEfficiency.unitsReplaced
                  : ''
              }
              onChange={handleInputUnitsChange}
              min="0"
              max={99999999.99}
              maxLength={10}
              onKeyDown={handleKeyDown}
              className="calculator-input"
            />
          </div>
        </div>
        <div className="row calculator-input-row">
          <div className="col-12 col-sm-5 col-md-3 col-lg-3">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Wattage of Conventional Units')}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-4 col-lg-2">
            <input
              title="Wattage of Conventional Units"
              type="number"
              id="wattegeInput"
              value={
                energyEfficiency.wattageConventional !== null
                  ? energyEfficiency.wattageConventional
                  : ''
              }
              onChange={handleInputWattageChange}
              min="0"
              maxLength={10}
              onKeyDown={handleKeyDown}
              className="calculator-input"
            />
          </div>
        </div>
        <div className="row calculator-input-row">
          <div className="col-12 col-sm-5 col-md-3 col-lg-3">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Wattage of Replacement Units')}
              {defaultUnits && (
                <div className="calculator-default-roadMaintenance">
                  (Default Watts, <NumberFormatterSinSeparador value={defaultUnits} />)
                </div>
              )}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-4 col-lg-2">
            <input
              ref={inputEnergyRef}
              type="number"
              id="wattageInput"
              value={inputValue !== null ? inputValue : ''}
              min="0"
              onChange={handleInputWattage}
              onBlur={handleBlurInput}
              className="calculator-input"
              disabled={isInputDisabled}
              title="Default Watts"
            />
            <UncontrolledTooltip
              className="tooltip-toolkit"
              placement="bottom"
              target="wattageInput"
              modifiers={tooltipPreventOverflowModifiers}
            >
              {t('Default factor')}
            </UncontrolledTooltip>
          </div>
          {isInputDisabled && (
            <>
              <button
                title="Edit"
                type="button"
                className="gi-icon"
                onClick={setCustomArea}
                id="editIcon_energy"
              >
                <FontAwesomeIcon icon="edit" />
              </button>
              <UncontrolledTooltip
                className="tooltip-toolkit"
                placement="bottom"
                target="editIcon_energy"
                modifiers={tooltipPreventOverflowModifiers}
              >
                {t('Custom watts')}
              </UncontrolledTooltip>
            </>
          )}
        </div>
        <div className="row calculator-input-row">
          <div className="col-12 col-sm-5 col-md-3 col-lg-3">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Hours of Operation per Day')}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-4 col-lg-2">
            <input
              title="Hours of Operation per Day"
              type="number"
              id="hoursInput"
              value={energyEfficiency.hoursDay !== null ? energyEfficiency.hoursDay : ''}
              onChange={handleInputHoursChange}
              min="0"
              maxLength={10}
              onKeyDown={handleKeyDown}
              className="calculator-input"
            />
          </div>
        </div>
      </div>
      <CalculatorTableWrapper
        totalCarbonSum={totalCarbonSum}
        onShowDetailsModal={() => setShowDetailsModal(true)}
        subtotalColSpan={3}
        subtotalLabel="Avoided Emissions"
        colGroup={
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
        }
      />
      <Modal
        title="Energy Efficiency Improvements"
        visible={showDetailsModal}
        setVisible={() => setShowDetailsModal(false)}
        actions={[{ text: 'Close', action: () => setShowDetailsModal(false) }]}
        customClass="extra_wide-modal calculator"
        onClosed={() => setShowDetailsModal(false)}
      >
        <div className="mb-0 table-responsive">
          <EnergyEfficiencyDetails
            energyEfficiency={energyEfficiency}
            defaultUnits={Number(defaultUnits)}
          />
        </div>
      </Modal>
    </>
  );
};

export default EnergyEfficiencySection;
